import { catchError, filter, of, switchMap, map, distinctUntilChanged } from 'rxjs';
import { ofType } from 'redux-observable';
import TypeaheadLocationType  from 'enums/typeaheadLocationType';
import facetsActionTypes from '../constants/actionTypeGroupsConstants';
import LocationType from '../enums/locationType';
import ResultType from '../enums/resultType';
import getCounts$ from '../apis/getCounts';
import getBrands$ from '../apis/getBrands';
import { facetsSlice, searchParametersSlice } from './slices';
import { facetsSelector, selectResultType, selectSearchParameters } from './selectors';
import FetchRequest from '../enums/fetchRequest';
import validateIfFetchRequest from '../services/fetchingValidator';

const actions = searchParametersSlice.actions;

// if the action is of type actions.fetchLocations.type, make sure that the location type is not 'Community'
// to prevent fetching new results when being redirected to the detail page.
const isCommunityLocationTypeSearch = (action) =>
  action.type === actions.fetchLocations.type &&
  action.payload.locationType.toLowerCase() === TypeaheadLocationType.Community.toString();

const isBuilderResultType = (resultType) => resultType === ResultType.Builders.toString();

// Epic to be able to know when the fetchResultListings should be executed. In other words, it checks all
// the actions and verify if it should execute the fetchResultListings action.
export const fetchResultListingsEpic = (action$) =>
  action$.pipe(
    filter((action) => validateIfFetchRequest(action.type, FetchRequest.Listings)),
    // if the action is of type actions.fetchLocations.type, make sure that the location type is not 'Community'
    // to prevent fetching new results when being redirected to the detail page.
    filter((action) => !isCommunityLocationTypeSearch(action)),
    switchMap(() => of(actions.fetchResultListings()))
  );

// Epic to be able to retrieve the result count information by checking when the fetchResultCounts action
// was executed. Then, according to the retrieving status, executes the action according to it.
// If it gets successful, it means this call was fulfilled, otherwise it means there was an error.
export const getResultCountsEpic = (action$, state$) =>
  action$.pipe(
    ofType(actions.fetchResultCounts.type),
    switchMap(() => {
      const payload = selectSearchParameters(state$.value);
      return getCounts$(payload).pipe(
        switchMap((data) => of(actions.fetchResultsCountsFulfilled(data))),
        catchError((error) => of(actions.fetchResultsCountsError(error)))
      );
    })
  );

// Epic to be able to know when the fetchResultCounts should be executed. In other words, it checks all
// the actions and verify if it should execute the fetchResultCounts action.
export const fetchResultCountsEpic = (action$) =>
  action$.pipe(
    filter((action) => validateIfFetchRequest(action.type, FetchRequest.Counts)),
    // if the action is of type actions.fetchLocations.type, make sure that the location type is not 'Community'
    // to prevent fetching new results when being redirected to the detail page.
    filter((action) => !isCommunityLocationTypeSearch(action)),
    switchMap(() => of(actions.fetchResultCounts()))
  );

// Epic to be able to retrieve the result facet information by checking when the fetchBrandsFaceting action
// was executed. Then, according to the retrieving status, executes the action according to it.
// If it gets successful, it means this call was fulfilled, otherwise it means there was an error.
export const getResultFacetsEpic = (action$, state$) =>
  action$.pipe(
    ofType(actions.fetchBrandsFaceting.type),
    switchMap(() => {
      const parameters = selectSearchParameters(state$.value);
      return getBrands$(parameters).pipe(
        switchMap((data) => of(actions.fetchResultFacetsFulfilled(data))),
        catchError((error) => of(actions.fetchResultFacetsError(error)))
      );
    })
  );

// Epic to be able to know when the fetchBrandsFaceting should be executed. In other words, it checks all
// the actions and verify if it should execute the fetchBrandsFaceting action.
export const fetchResultFacetsEpic = (action$, state$) =>
  action$.pipe(
    filter((action) => validateIfFetchRequest(action.type, FetchRequest.SchoolsAndBrandsFaceting)),
    // if the action is of type actions.fetchLocations.type, make sure that the location type is not 'Community'
    // to prevent fetching new results when being redirected to the detail page.
    filter((action) => !isCommunityLocationTypeSearch(action)),
    // As the faceting is not being shown when the builder result type is shown, we don't have to keep doing
    // result facets calls while it is builder result type.
    filter(() => !isBuilderResultType(selectResultType(state$.value))),
    switchMap(() => of(actions.fetchBrandsFaceting()))
  );

const filters = facetsSlice.actions;

// Gets the current state of the facets using a selector
function getFacetsStatus(state) {
  return facetsSelector(state);
}

/**
 * Epic that updates the filters when any of the facets actions is dispatched
 * @param {*} action$
 * @param {*} state$
 * @returns a new action of type updateFilters, with the payload being the current state of the facets
 */
export const updateFacetsEpic = (action$, state$) =>
  action$.pipe(
    filter((action) => facetsActionTypes.includes(action.type)),
    // if the action is of type actions.fetchLocations.type, make sure that the location type is not 'Community'
    // to prevent fetching new results when being redirected to the detail page.
    filter((action) => !isCommunityLocationTypeSearch(action)),
    map(() => ({
      type: filters.updateFilters.type,
      payload: getFacetsStatus(state$.value),
    }))
  );
/**
 * Epic that updates the location when result type is changed to Builder directory.
 * By biz rule, when there is a tab change (result type) to builder directory, with a zip location context,
 * it should be redirected to its city
 * @param {*} action$
 * @param {*} state$
 */
export const updateZipSearchesOnBuildersResult = (action$, state$) =>
  action$.pipe(
    ofType(actions.fetchResultsType.type),
    filter((action) => action.payload.resultType === ResultType.Builders.toString()),
    filter(() => state$.value?.searchParameters.location.locationType === LocationType.Zip.toString()),
    distinctUntilChanged(),
    switchMap(() => of(actions.changeZipSearchtoCitySearch()))
  );

  /**
 * Epic that updates the location when result type is changed to Builder directory.
 * By biz rule, when there is a tab change (result type) to builder directory, with a zip location context,
 * it should be redirected to its city
 * @param {*} action$
 * @param {*} state$
 */
export const updateNeighborhoodSearchesOnBuildersResult = (action$, state$) =>
  action$.pipe(
    ofType(actions.fetchResultsType.type),
    filter((action) => action.payload.resultType === ResultType.Builders.toString()),
    filter(() => state$.value?.searchParameters.location.locationType === LocationType.Neighborhood.toString()),
    distinctUntilChanged(),
    switchMap(() => of(actions.changeNeighborhoodSearchtoCitySearch()))
  );

/**
 * Epic that updates the location when result type is changed to Builder directory.
 * By biz rule, when there is a tab change (result type) to builder directory, with a school location context,
 * it should be redirected to its market
 * @param {*} action$
 * @param {*} state$
 */
export const updateSchoolDistrictSearchesOnBuildersResult = (action$, state$) =>
  action$.pipe(
    ofType(actions.fetchResultsType.type),
    filter((action) => action.payload.resultType === ResultType.Builders.toString()),
    filter(() => state$.value?.searchParameters.location.locationType === LocationType.SchoolDistrict.toString()),
    distinctUntilChanged(),
    switchMap(() => of(actions.changeSchoolDistrictSearchtoMarketSearch()))
  );